import React, { useEffect, useRef, useState } from "react";
import {
  VerifyEmailFormBox,
  VerifyEmailMainBox,
  customStyles,
} from "./verifyEmailStyles";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Modal, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { sendEmailVerification } from "firebase/auth";
import { useAuthValue } from "../../../../Infrastructure/States/authContext";
import { closeChooseModal } from "../../../../Infrastructure/States/authModalsSlice";
import { ShowErrorToast } from "../../Toast/toast";
import ERROR_CONSTANTS from "../../../../Infrastructure/Constants/errorConstants";
import { USER_LOGS_CLIENT } from "../../../../Infrastructure/Constants/logsConstant";
import useAuthentication from "../../../../Infrastructure/States/onAuthStateChange";
import { auth, database } from "../../../../Infrastructure/config";
import { ref, update } from "firebase/database";
import paths from "../../../../Infrastructure/Router/paths";
import { safeClientLogEvent } from "../../../../Infrastructure/Utilities/safeClientLogEvent";

export const VerifyEmail = () => {
  const user = auth.currentUser;
  const {userId} = useAuthentication()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [time, setTime] = useState(60);
  const userEmailAddress = user?.email;
  const emailVerifiedRef = useRef(null)
  const startTimeRef = useRef(Date.now())
  const ip = useSelector((state) => state.ip.address)
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { currentUser, timeActive, setTimeActive } = useAuthValue();
  const isOpenModal = useSelector((state) => state.auth.isOpenModal);
  const userData = useSelector((state) => state.userData.data);
  const email_verified = userData?.email_verified
  const selectedRoute = useSelector(
    (state) => state.auth.selectedRouteBeforeVerified
  );
    const handleCloseModal = () => {
    dispatch(closeChooseModal());
  };
  const handleVerifiedEmailAction = () => {
    handleCloseModal();
    navigate(paths.dashboardPage.path);
  };
  const handleFirebaseErrors = (error) => {
    if (!error) {
      return;
    }
    let errorMessage = ERROR_CONSTANTS.GENERIC_ERROR_TOAST;
    switch (error.code) {
      case "auth/wrong-password":
        errorMessage =
          "Invalid password. Please check your password and try again.";
        break;
      case "auth/user-not-found":
        errorMessage = "User not found! Please Sign Up.";
        break;
      case "auth/too-many-requests":
        errorMessage =
          error?.error?.message || "Too many requests. Please try again later.";
        break;
      default:
        ShowErrorToast(error);
    }
    ShowErrorToast(errorMessage);
  };
  const resendEmailVerification = async () => {
    try {
      await sendEmailVerification(auth.currentUser)
      setTimeActive(true);
      await safeClientLogEvent({
        ...USER_LOGS_CLIENT.EMAIL_VERIFICATION_RESEND_SUCCESS,
        uid: userId || null,
        source: "verifyEmail.jsx",
        emailInfo: { email: userEmailAddress },
        metadata: null,
        error: null
      });
    } catch(err) {
      handleFirebaseErrors(err)
      await safeClientLogEvent({
        ...USER_LOGS_CLIENT.EMAIL_VERIFICATION_RESEND_FAILED,
        uid: userId || null,
        source: "verifyEmail.jsx",
        emailInfo: { email: userEmailAddress },
        metadata: null,
        error: err?.message || null
      });
    }
  };

  useEffect(() => {
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);

      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);

      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }, []);

    useEffect(() => {
    if (!isOnline || !currentUser) return;
    const interval = setInterval(() => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTimeRef.current;
      if (elapsedTime > 3 * 24 * 60 * 60 * 1000) {
        clearInterval(interval);
        return;
      }
      currentUser
        ?.reload()
        .then(async () => {
          const emailVerified = currentUser?.emailVerified;
          if (emailVerifiedRef.current !== emailVerified) {
            emailVerifiedRef.current = emailVerified;
            const userRef = ref(database, `users/${currentUser.uid}`);
            await update(userRef, { email_verified: emailVerified })
          }
          if (emailVerified) {
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.EMAIL_VERIFICATION_SUCCESS,
              uid: userId || null,
              source: "VerifyEmail.jsx",
              emailInfo: { email: userEmailAddress },
              metadata: null,
              error: null
            });
            handleCloseModal()
            setTimeout(() => {
              selectedRoute && navigate(selectedRoute);
            }, 300)
            clearInterval(interval);
          }
        })
        .catch(async (err) => {
        if (
          err.code === "auth/network-request-failed" ||
          err.message?.toLowerCase().includes("network") ||
          err.message?.toLowerCase().includes("internet")
        ) {
          return;
        }
        ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST);
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.EMAIL_VERIFICATION_CHECK_FAILED,
          uid: userId || null,
          source: "VerifyEmail.jsx",
          emailInfo: { email: userEmailAddress },
          metadata: null,
          error: err?.message || null
        });
        });
    }, 1000);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, selectedRoute, navigate, isOnline, userId, ip, userEmailAddress]);

  useEffect(() => {
    let interval = null;
    if (timeActive && time !== 0) {
      interval = setInterval(() => {
        setTime((time) => time - 1);
      }, 1000);
    } else if (time === 0) {
      setTimeActive(false);
      setTime(60);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timeActive, time, setTimeActive]);

  return (
    <Box>
      {/* Email Verification Modal */}
      <Modal
        disableScrollLock
        open={isOpenModal}
        onClose={handleCloseModal}
        aria-labelledby="login-modal-title"
        aria-describedby="login-modal-description"
        sx={{...customStyles.modal}}
        BackdropProps={{
          sx: customStyles.backdrop,
        }}
      >
        <VerifyEmailMainBox>
          <Box
            px={2}
            onClick={handleCloseModal}
            sx={{
              position: "relative",
              top: "9px",
              right: "-142px",
              cursor:"pointer"
            }}
          >
            <CloseIcon/>
          </Box>
          <Typography
            variant="h1"
            p={2}
            color="primary"
            sx={{ fontSize: "30px" }}
          >
            Verify Account
          </Typography>
          <VerifyEmailFormBox component="form" noValidate>
            <Box
              p={2}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-around"}
              alignItems={"center"}
            >
              <Box pt={2} pb={2}>
                {/* Display email verification status */}
                {email_verified ? (
                  <Typography
                    variant="body2"
                    color="secondary"
                    sx={{
                      textDecoration: "none",
                      fontSize: "16px",
                      textAlign: "center",
                    }}
                  >
                    Email verified successfully now you can proceed to the
                    dashboard.
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      color="secondary"
                      sx={{
                        textDecoration: "none",
                        fontSize: "16px",
                        textAlign: "center",
                      }}
                    >
                      Verification Link sent to email <br />
                    </Typography>
                    <Typography
                      variant="body2"
                      color="secondary"
                      sx={{
                        textDecoration: "none",
                        fontSize: "16px",
                        textAlign: "center",
                        fontWeight: "900",
                        paddingTop: "4px",
                      }}
                    >
                      {userEmailAddress}
                    </Typography>
                  </>
                )}
              </Box>
              {email_verified ? (
                <>
                  <Button
                    variant="contained"
                    onClick={handleVerifiedEmailAction}
                  >
                    Go to Dashboard
                  </Button>
                </>
              ) : (
                <Box
                  pt={2}
                  pb={2}
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  sx={{ width: "200px" }}
                >
                  <Button
                    variant="contained"
                    onClick={resendEmailVerification}
                    disabled={timeActive}
                  >
                    Resend Email {timeActive && time}
                  </Button>
                </Box>
              )}
              <Typography
                variant="body2"
                color={"secondary"}
                sx={{
                  textDecoration: "none",
                  fontSize: "16px",
                  textAlign: "center",
                  paddingTop: "20px",
                }}
              >
                {email_verified
                  ? "Enhance your skills with Vulcan Learning"
                  : "Not Seeing the email? Ensure your email is correct and check the spam/junk folder."}
              </Typography>
            </Box>
          </VerifyEmailFormBox>
        </VerifyEmailMainBox>
      </Modal>
    </Box>
  );
};
